/**
 * Trust / benefits strip at the foot of the storefront home page
 * (Free Shipping · Premium Quality · Secure Payment).
 *
 * The backend owns the content: GET /settings/app returns
 * `home_trust_badges` — the sections to draw, already in display order, with
 * every default applied (see TRUST_BADGE_DEFAULTS in
 * backend/app/routes/settings.py). This module turns that into something
 * that is always safe to render, and holds the one thing only the frontend
 * can know: which icon component each icon key stands for.
 *
 * No module-level state — everything is derived from the branding object the
 * caller passes in, so nothing can carry over from one tenant to another.
 */
import {
  Award, BadgeCheck, Clock, CreditCard, Gem, Gift, Headphones, Heart, Leaf, Lock,
  Package, RotateCcw, ShieldCheck, Sparkles, Star, ThumbsUp, Truck, Wallet,
} from "lucide-react";

/** Where the Free Shipping amount goes in its description. */
export const AMOUNT_TOKEN = "{amount}";
const AMOUNT = /\{\s*amount\s*\}/i;
const AMOUNT_ALL = /\{\s*amount\s*\}/gi;

// Keys must match TRUST_BADGE_ICONS in backend/app/routes/settings.py.
export const TRUST_BADGE_ICONS = {
  truck: { label: "Delivery truck", Icon: Truck },
  package: { label: "Package", Icon: Package },
  sparkles: { label: "Sparkle", Icon: Sparkles },
  "shield-check": { label: "Shield", Icon: ShieldCheck },
  lock: { label: "Lock", Icon: Lock },
  "credit-card": { label: "Card", Icon: CreditCard },
  wallet: { label: "Wallet", Icon: Wallet },
  award: { label: "Award", Icon: Award },
  "badge-check": { label: "Verified badge", Icon: BadgeCheck },
  gem: { label: "Gem", Icon: Gem },
  star: { label: "Star", Icon: Star },
  heart: { label: "Heart", Icon: Heart },
  "thumbs-up": { label: "Thumbs up", Icon: ThumbsUp },
  gift: { label: "Gift", Icon: Gift },
  leaf: { label: "Leaf", Icon: Leaf },
  clock: { label: "Clock", Icon: Clock },
  returns: { label: "Returns", Icon: RotateCcw },
  support: { label: "Support", Icon: Headphones },
};

/** Theme presets whose home page draws the strip. */
export const TRUST_BADGE_PRESETS = ["asfashion"];

/**
 * Used ONLY when the settings response carries no `home_trust_badges` at all
 * (settings could not be loaded, or an older backend mid-deploy). Mirrors the
 * backend defaults so the storefront looks the same either way.
 */
export const DEFAULT_TRUST_BADGES = [
  {
    key: "free_shipping", icon: "truck", title: "Free Shipping", threshold: 2500,
    description: "Free express shipping on all orders above {amount}.",
  },
  {
    key: "premium_quality", icon: "sparkles", title: "Premium Quality",
    description: "5-point quality check on fabric, stitching and dye fastness.",
  },
  {
    key: "secure_payment", icon: "shield-check", title: "Secure Payment",
    description: "Encrypted payments via UPI, Cards and NetBanking.",
  },
];

const DEFAULT_BY_KEY = new Map(DEFAULT_TRUST_BADGES.map((b) => [b.key, b]));
const text = (v) => (typeof v === "string" ? v.trim() : "");
const amount = (v) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);
// Own keys only: "constructor" and friends are not icons.
const iconFor = (k) => (Object.prototype.hasOwnProperty.call(TRUST_BADGE_ICONS, k) ? TRUST_BADGE_ICONS[k].Icon : null);

export function hasAmountToken(description) {
  return AMOUNT.test(description || "");
}

/** Replace every `{amount}` in a description with the formatted amount. */
export function fillAmount(description, threshold, formatAmount) {
  return String(description || "").replace(AMOUNT_ALL, () => formatAmount(threshold));
}

/** The component for an icon key; never undefined, whatever the key. */
export function trustBadgeIcon(icon, fallback) {
  return iconFor(icon) || iconFor(fallback) || Sparkles;
}

/**
 * The badges to draw, in order: `[{ key, Icon, title, description }]`.
 *
 * `badges` is `branding.home_trust_badges`. An array is taken as the tenant's
 * decision (an empty one means "show none"); anything else means "not
 * provided" and falls back to the defaults. `formatAmount` is the store's
 * currency formatter (`money` from lib/api).
 */
export function resolveTrustBadges(badges, formatAmount) {
  const list = Array.isArray(badges) ? badges : DEFAULT_TRUST_BADGES;
  const seen = new Set();
  return list.reduce((out, b) => {
    if (!b || typeof b !== "object" || typeof b.key !== "string" || b.enabled === false || seen.has(b.key)) return out;
    const fallback = DEFAULT_BY_KEY.get(b.key) || {};
    const title = text(b.title) || fallback.title;
    const description = text(b.description) || fallback.description;
    if (!title || !description) return out;
    seen.add(b.key);
    // Only a section that carries an amount has one to substitute.
    const threshold = amount(b.threshold) ?? fallback.threshold;
    out.push({
      key: b.key,
      Icon: trustBadgeIcon(b.icon, fallback.icon),
      title,
      description: threshold == null ? description : fillAmount(description, threshold, formatAmount),
    });
    return out;
  }, []);
}
